// src/components/AboutSection.js

import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FiCode,
  FiSmile,
  FiTarget,
  FiLayers,
} from 'react-icons/fi';

// ────── Keyframes ──────
const gradientFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// ────── Layout & Background ──────
const SectionContainer = styled.section`
  padding: var(--spacing-xxl) 0;
  position: relative;
  overflow: hidden;
  /* Dark gradient matching Products section */
  background: linear-gradient(
    -45deg,
    #0B1D3A,
    #152E4F,
    #0B1D3A
  );
  background-size: 300% 300%;
  animation: ${gradientFlow} 20s ease infinite;
`;

const ParallaxBackground = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 80% 10%, rgba(71, 181, 255, 0.08) 0%, rgba(71, 181, 255, 0) 30%),
    radial-gradient(circle at 10% 90%, rgba(26, 54, 93, 0.1) 0%, rgba(26, 54, 93, 0) 30%),
    radial-gradient(circle at 50% 30%, rgba(78, 205, 196, 0.05) 0%, rgba(78, 205, 196, 0) 30%);
  opacity: 1;
  z-index: 0;
`;

const ParallaxShape = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  top: ${(props) => props.top || '20%'};
  left: ${(props) => props.left || 'auto'};
  right: ${(props) => props.right || 'auto'};
  background: ${(props) => props.background || 'rgba(90, 189, 255, 0.03)'};
  border-radius: ${(props) => props.borderRadius || '50%'};
  z-index: 0;
  transform: translateY(var(--parallax-offset, 0px)) scale(calc(1 + (var(--scale-ratio, 0) * 0.04)));
    transition: transform var(--transition-normal);
  will-change: transform;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

// Single-column layout
const SingleColumnLayout = styled.div`
  width: 100%;
`;

// ────── Typography & Text Blocks ──────
const SectionTitle = styled.h2`
  margin-bottom: var(--spacing-md);
  position: relative;
  color: var(--white);

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, var(--accent), rgba(71, 181, 255, 0.3));
    border-radius: 2px;
  }

  @media (max-width: 992px) {
    text-align: center;
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const AboutText = styled.p`
  margin: var(--spacing-lg) 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  line-height: 1.8;

  @media (max-width: 992px) {
    text-align: center;
  }
`;

// ────── Values Grid ──────
const ValuesList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
`;

const ValueIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  background-color: rgba(71, 181, 255, 0.15);
  color: var(--accent);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-right: var(--spacing-md);
`;

const ValueContent = styled.div``;

const ValueTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: var(--spacing-xs);
  color: var(--white);
`;

const ValueDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.6;
`;

// ────── Vision & Badge (Glassmorphism) ──────
const VisionSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  margin-top: var(--spacing-xl);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
`;

const VisionTitle = styled.h3`
  margin-bottom: var(--spacing-md);
  color: var(--white);
  font-size: 1.3rem;
`;

const VisionText = styled.p`
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;
`;

// ────── Animations ──────
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

// ────── Component ──────
const AboutSection = () => {
  const [sectionRef, sectionInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [valuesRef, valuesInView] = useInView({ threshold: 0.1, triggerOnce: true });

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const section = document.getElementById('about');
      if (!section) return;

      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const relativeScroll = scrollPosition - sectionTop + window.innerHeight;
      const ratio = Math.min(
        Math.max(relativeScroll / (sectionHeight + window.innerHeight), 0),
        1,
      );

      document.documentElement.style.setProperty('--parallax-offset', `${ratio * -120}px`);
      document.documentElement.style.setProperty('--shape-offset', `${ratio * -60}px`);
      document.documentElement.style.setProperty('--scale-ratio', ratio);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const values = [
    {
      icon: <FiCode />,
      title: 'Innovation First',
      description:
        'We create products that solve real problems with innovative, technology-driven solutions.',
    },
    {
      icon: <FiSmile />,
      title: 'User Centered',
      description:
        'We obsess over user experience, continuously refining our apps based on user feedback and behavior.',
    },
    {
      icon: <FiTarget />,
      title: 'Quality Focused',
      description:
        'We maintain high standards in design, development, and performance for every product we build.',
    },
    {
      icon: <FiLayers />,
      title: 'Simplicity',
      description:
        'We believe in creating intuitive interfaces that make complex tasks simple and accessible for everyone.',
    },
  ];

  return (
    <SectionContainer id="about" ref={sectionRef}>
      <ParallaxBackground />
      <ParallaxShape top="15%" right="10%" background="rgba(71, 181, 255, 0.07)" />
      <ParallaxShape top="70%" left="5%" background="rgba(26, 54, 93, 0.05)" />
      <ParallaxShape
        top="40%"
        right="30%"
        background="rgba(78, 205, 196, 0.06)"
        borderRadius="30%"
      />

      <ContentWrapper>
        <SingleColumnLayout>
          <motion.div
            initial="hidden"
            animate={sectionInView ? 'visible' : 'hidden'}
            variants={fadeInLeft}
          >
            <SectionTitle>About XADevs</SectionTitle>
            <AboutText>
              XADevs LLC is a US-registered software company that builds AI products and
              software systems: AI-native product features, plus the cloud backends and
              cross-platform apps that deliver them.
            </AboutText>

            <AboutText>
              Our flagship product, VolpeTrack, is an AI-powered personal finance assistant:
              it categorizes expenses automatically, analyzes spending patterns, and generates
              personalized financial insights with a built-in AI assistant. HoldThen, our B2B
              logistics platform, applies the same engineering approach to international
              package forwarding and shipment tracking.
            </AboutText>

            <AboutText>
              Everything we ship runs on infrastructure we design and operate: scalable cloud
              services, data pipelines, and AI integrations built for production, not demos.
            </AboutText>
          </motion.div>

          <ValuesList
            ref={valuesRef}
            as={motion.div}
            initial="hidden"
            animate={valuesInView ? 'visible' : 'hidden'}
            variants={staggerContainer}
          >
            {values.map((val, i) => (
              <ValueItem key={i} variants={fadeIn}>
                <ValueIcon>{val.icon}</ValueIcon>
                <ValueContent>
                  <ValueTitle>{val.title}</ValueTitle>
                  <ValueDescription>{val.description}</ValueDescription>
                </ValueContent>
              </ValueItem>
            ))}
          </ValuesList>

          <VisionSection>
            <VisionTitle>Our Vision</VisionTitle>
            <VisionText>
              Our current products focus on personal finance and logistics, and we aim to build a broader portfolio of useful
              applications that solve real operational problems while staying true to our core
              values of innovation, user-centered design, and quality.
            </VisionText>
          </VisionSection>
        </SingleColumnLayout>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default AboutSection;
